import React, { useEffect, useState } from 'react';
import { Button, TextField, Select, MenuItem, InputLabel, FormControl, Autocomplete, InputAdornment } from '@mui/material';
import {Launch as LaunchIcon, Close as CloseIcon} from '@mui/icons-material';
import CommonDialog from '../CommonDialog/CommonDialog';
import { getLibraryByUserId } from '../../services/libraryService';
import { getCellsByLibraryId } from '../../services/cellService';
import { getAllViewTypes } from '../../services/viewTypeService';
import { getViewsByCellId } from '../../services/viewService';
import { PREDEFINED_LIBRARIES, VIEW_TYPES } from '../../constants/Constants';


const NewCellDialog = ({ open, onClose, onCreate, cellViewPayload, onStickCreate, onOpenManageLibrary }) => {
  const [library, setLibrary] = useState('');
  const [libraries, setLibraries] = useState([]);
  const [cellName, setCellName] = useState('');
  const [cellId, setCellId] = useState('');
  const [cells, setCells] = useState([]);
  const [selectedViewType, setSelectedViewType] = useState('');
  const [viewTypes, setViewTypes] = useState([]);
  const [viewName, setViewName] = useState('');
  const [views, setViews] = useState([]);
  const [autoViewNameEdit, setAutoViewNameEdit] = useState(false);
  const [cellNameError, setCellNameError] = useState('');
  const [viewNameError, setViewNameError] = useState('');
  
  const userId = localStorage.getItem('user_id')

  useEffect(() => {
    if(open){
      fetchLibraries();
      handleFetchViewTypes();
    }
  }, [open]);

  // Fetch cells when library changes
  useEffect(() => {
    if (library) {
      fetchCells(library);
      setCellName('');
      setCellId('');
      setViews([]);
    }
  }, [library]);

  // Fetch views when cellId changes
   useEffect(() => {
    if (cellId) {
      setViews([]);
      fetchViews(cellId);
    }
  }, [cellId]);

  const fetchLibraries = async () => {
    try {
      const response = await getLibraryByUserId(userId);
      if (!response.success) return;            
      const data = response.body;
      setLibraries(data);
    } catch (error) {
      console.error('Failed to load libraries:', error);
      setLibraries([]);
    }
  };

  const fetchCells = async (libraryId) => {
    try {
      const response = await getCellsByLibraryId(libraryId);
      if(!response.success) return;    
      const data = response.body;
      setCells(data);
    } catch (error) {
      console.error('Failed to load cells:', error);
      setCells([]);
    }
  };

  const handleFetchViewTypes = async () => {
    try {
      let response = await getAllViewTypes();
      if(!response.success) return;    
      var data = response.body;

      data = data.filter(a => a.name.toLowerCase() !== 'symbol');
      setViewTypes(data);

      if (data.length > 0 && !selectedViewType) {
      setSelectedViewType(data[0].view_type_id);
      setViewName('schematic');
    }

    } catch (error) {
      console.error('Failed to load view types:', error);
      setViewTypes([]);
    }
  };

  const fetchViews = async () => {
    try {
      const response = await getViewsByCellId(cellId);
      if(!response.success) return;
      const data = response.body;
      setViews(data);
    } catch (error) {
      console.error('Failed to load views for library:', error);
      setViews([]);
    }
  };

  const handleViewTypeChange = (e) => {
    const selectedViewId = e.target.value;
    setSelectedViewType(selectedViewId);
    setViewNameError('');

      const type = viewTypes.find(v => v.view_type_id === selectedViewId);
      if (type?.name.toLowerCase() === 'stick') {
        setViewName('stick');
      } else {
        setViewName('schematic');
    }
  };

  const validateCellName = (name) => {
    const trimmed = name.trim().toLowerCase();

      if (!trimmed) {
        setCellNameError('Cell name cannot be empty.');
        return;
      }

      if (cellId) {
        setCellNameError('');
        return;
      }

        const duplicateInCells = cells.find(cell => cell.name.trim().toLowerCase() === trimmed);
        const duplicate = Object.values(cellViewPayload).find(
          payload =>
            payload.library_id === library &&
            payload.cell_name.trim().toLowerCase() === trimmed
        );

    if (duplicateInCells || duplicate) {
      setCellNameError('Cell name already exists in this library.');
    } else {
      setCellNameError('');
    }
  };

  const validateViewName = (name) => {
    const trimmed = name.trim().toLowerCase();

    if (!trimmed) {
      setViewNameError('View name cannot be empty.');
      return;
    }

    const viewType = viewTypes.find(v => v.view_type_id === selectedViewType);
    if (viewType) {
      const typeName = viewType.name.toLowerCase();

      if (typeName === 'schematic' && !/^schematic.*/.test(trimmed)) {
        setViewNameError('View name must start with "schematic".');
        return;
      }

      if (typeName === 'stick' && !/^stick.*/.test(trimmed)) {
        setViewNameError('View name must start with "stick".');
        return;
      }
    }

    const duplicate = views.find(view => view.name.toLowerCase() === trimmed)
      || Object.values(cellViewPayload).find(
          payload =>
            payload.cell_id === cellId &&
            payload.view_name?.trim().toLowerCase() === trimmed
        );

    if (duplicate) {
      setViewNameError('View name already exists in this cell.');
    } else {
      setViewNameError('');
    }
  };

  const handleCreate = () => {
    validateViewName(viewName);

    if (!autoViewNameEdit && cellId) {
      const trimmedValue = viewName.trim().toLowerCase();
      const alreadyExists = views.some(v => v.name.toLowerCase() === trimmedValue) ||
        Object.values(cellViewPayload).some(
          payload =>
            payload.cell_id === cellId &&
            payload.view_name?.trim().toLowerCase() === trimmedValue
        );

      if (alreadyExists) {
        setViewNameError('View name already exists in this cell.');
        return;
      }
    }

    if (cellNameError || viewNameError || !cellName.trim() || !viewName.trim() || !library || !selectedViewType) {
      return;
    }

    if(selectedViewType === VIEW_TYPES.stick){
      const payload = {
        library_id: library,
        library_name: libraries.find(lib => lib.library_id === library)?.name || '',
        cell_id: cellId,
        cell_name: cellName,
        view_name: viewName,
      };
      onStickCreate(payload);
      onClose();
    }
    else {
      const payload = {
        library_id: library,
        cell_name: cellName,
        cell_id: cellId,
        view_name: viewName,
        view_type_id: selectedViewType,
      };

      onCreate(payload);
      onClose();
    }
    resetFields();
  };

  const handleClose = () => {
    onClose();
    resetFields();
  }

  const resetFields = () => {
    setLibrary('');
    setCellName('');
    setCellId('');
    setCells([]);
    setViews([]);
    setCellNameError('');
    setViewNameError('');
    setAutoViewNameEdit(false);
    const defaultType = viewTypes[0];
    if (defaultType) {
      setSelectedViewType(defaultType.view_type_id);
      const defaultViewName = defaultType.name.toLowerCase() === 'stick' ? 'stick' : 'schematic';
      setViewName(defaultViewName);
    } else {
      setSelectedViewType('');
      setViewName('');
    }
  };

  return (
    <CommonDialog open={open} onClose={handleClose} title="New Cell" hideBackDrop={false}>
      <>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Library/Project Name</InputLabel>
          <Select
            value={library}
            onChange={(e) => {
              setLibrary(e.target.value)
              setViewNameError('');
            }}
            label="Library/Project Name"
          >
          {libraries.length === 0 ? (
            <MenuItem value="" sx={{ fontSize: '12px', cursor: 'default' }}>
              <span style={{ color: "#FF0000" }}>
                No libraries yet! {' '}
                <span
                  style={{ cursor: 'pointer', color: '#0000ff' }}
                  onClick={(e) => {
                    handleClose();
                    onOpenManageLibrary();
                  }}
                >
                  Create
                </span>
              </span>
            </MenuItem>
          ) : (
            libraries.map((lib) => (
              <MenuItem key={lib.library_id} value={lib.library_id}>
                {lib.name}
              </MenuItem>
            )))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <Autocomplete
            freeSolo
            options={cells} // pass full objects, not just names
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
            value={
              typeof cellName === 'string'
                ? { name: cellName }
                : cells.find(cell => cell.cell_id === cellId) || { name: '' }
            }
            onChange={(event, newValue) => {
              if (newValue && typeof newValue === 'object') {
                // User selected from dropdown
                setCellName(newValue.name);
                setCellId(newValue.cell_id);
                setCellNameError('');
                setViewNameError('');
              } else if (typeof newValue === 'string') {
                // User typed new name
                setCellName(newValue);
                setCellId(null);
                validateCellName(newValue);
                setViewNameError('');
              } else {
                setCellName('');
                setCellId(null);
                setCellNameError('');
                setViewNameError('');
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
              setCellName(newInputValue);
              setCellId(null); // Clear ID when typing manually
              validateCellName(newInputValue);
              setViewNameError('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cell Name"
                required
                error={!!cellNameError}
                helperText={cellNameError}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 75
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: "8px",
                  }
                }}
              />
            )}
          />
        </FormControl>
        
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="view-type-label">Type</InputLabel>
          <Select
            labelId="view-type-label"
            id="view-type-select"
            value={selectedViewType}
            onChange={handleViewTypeChange}
            label="Type"
          >
            {viewTypes.map((type) => (
              <MenuItem key={type.view_type_id} value={type.view_type_id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
        <TextField
          label="View Name"
          value={viewName}
          onChange={(e) => {
            const val = e.target.value;
            setViewName(val);
            setAutoViewNameEdit(true);
            validateViewName(val);
          }}
          required
          error={!!viewNameError}
          helperText={viewNameError}
          inputProps={{ maxLength: 20 }}
        />
        </FormControl>

      </>
      <>
        <Button variant="contained" className="customButtonStyle" onClick={handleClose} startIcon={<CloseIcon />}>Cancel</Button>
        <Button variant="contained" className="customButtonStyle"
          startIcon={<LaunchIcon />}
          disabled={!!cellNameError || !!viewNameError || !cellName.trim() || !viewName.trim() || !library || !selectedViewType}
          onClick={handleCreate}>Launch</Button>
      </>
    </CommonDialog>
  );
};

export default NewCellDialog;
